import React, { useState, useRef, useEffect } from "react";
import { DateRangePicker, createStaticRanges } from "react-date-range";
import {
  startOfDay,
  endOfDay,
  startOfMonth,
  endOfMonth,
  subDays,
  subMonths,
  subYears,
  format
} from "date-fns";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";

const myStaticRanges = [
  {
    label: "Today",
    range: () => ({ startDate: startOfDay(new Date()), endDate: endOfDay(new Date()) })
  },
  {
    label: "Last 30 Days",
    range: () => ({ startDate: subDays(startOfDay(new Date()), 29), endDate: endOfDay(new Date()) })
  },
  {
    label: "This Month",
    range: () => ({ startDate: startOfMonth(new Date()), endDate: endOfMonth(new Date()) })
  },
  {
    label: "Last Month",
    range: () => ({ startDate: startOfMonth(subMonths(new Date(), 1)), endDate: endOfMonth(subMonths(new Date(), 1)) })
  },
  {
    label: "Last 90 Days",
    range: () => ({ startDate: subDays(startOfDay(new Date()), 89), endDate: endOfDay(new Date()) })
  },
  {
    label: "Last 6 Months",
    range: () => ({ startDate: startOfMonth(subMonths(new Date(), 6)), endDate: endOfMonth(new Date()) }) // approximate
  },
  {
    label: "Last 1 Year",
    range: () => ({ startDate: startOfMonth(subYears(new Date(), 1)), endDate: endOfMonth(new Date()) })
  }
];

export default function TicketsHeader({ onFilterChange, filters }) {
  const [showPicker, setShowPicker] = useState(false);
  const [rangeState, setRangeState] = useState([
    {
      startDate: new Date(),
      endDate: new Date(),
      key: "selection"
    }
  ]);
  const pickerRef = useRef(null);

  // Default filter state
  const defaultFilters = {
    duration: "All Time",
    status: "All",
    title: "",
  };

  // Check if any filters are applied
  const hasActiveFilters = () => {
    return (
      filters?.status !== defaultFilters.status ||
      filters?.title !== defaultFilters.title ||
      filters?.from_date ||
      filters?.to_date
    );
  };

  const handleClearAllFilters = () => {
    onFilterChange("clearAll", defaultFilters);
    setShowPicker(false);
  };

  const handleApply = () => {
    const { startDate, endDate } = rangeState[0];
    onFilterChange("dateRange", {
      from_date: format(startDate, "yyyy-MM-dd"),
      to_date: format(endDate, "yyyy-MM-dd")
    });
    setShowPicker(false);
  };

  const handleCancel = () => {
    setShowPicker(false);
  };

  const getDisplayText = () => {
    if (filters?.from_date && filters?.to_date) {
      return `${filters.from_date} - ${filters.to_date}`;
    }
    return "Start Date To End Date";
  };

  const handleStatusChange = (e) => {
    onFilterChange("status", e.target.value);
  };

  const handleSearchChange = (e) => {
    onFilterChange("title", e.target.value);
  };

  // Close picker on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setShowPicker(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <>
      <div className="w-full bg-white mb-6 font-ubuntu pt-4">
        <div className="w-full" style={{ padding: "0" }}>
          <div className="flex items-center gap-x-4">
            {/* Duration filter */}
            <div className="flex items-center relative" ref={pickerRef}>
              <span className="text-gray-600 font-medium mr-2" style={{ fontSize: "14px" }}>
                Duration
              </span>
              <button
                className="flex items-center border border-gray-200 rounded px-3 py-1.5 text-sm text-black hover:bg-gray-50 bg-white"
                onClick={() => setShowPicker(!showPicker)}
              >
                <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                </svg>
                {getDisplayText()}
              </button>

              {showPicker && (
                <div className="absolute top-full left-0 mt-1 bg-white shadow-lg rounded-md border border-gray-200 z-50">
                  <DateRangePicker
                    onChange={(item) => setRangeState([item.selection])}
                    showSelectionPreview={true}
                    moveRangeOnFirstSelection={false}
                    months={2}
                    ranges={rangeState}
                    direction="horizontal"
                    staticRanges={createStaticRanges(myStaticRanges)}
                    inputRanges={[]}
                  />
                  <div className="flex justify-end p-3 border-t border-gray-100 items-center bg-white gap-2">
                    <button
                      onClick={handleCancel}
                      className="px-4 py-1.5 border border-gray-200 text-gray-600 rounded text-sm hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleApply}
                      className="px-4 py-1.5 bg-red-400 text-white rounded text-sm hover:bg-red-500 font-medium"
                    >
                      Apply
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Status section with dropdown */}
            <div className="flex items-center">
              <span className="text-gray-600 font-medium mr-2" style={{ fontSize: "14px" }}>
                Status
              </span>
              <div className="inline-block relative">
                <div className="flex items-center border border-gray-200 rounded py-1.5 px-3 text-sm bg-white">
                  <select
                    className="w-full border-none text-gray-700 appearance-none bg-white focus:outline-none pr-4"
                    value={filters?.status || "All"}
                    onChange={handleStatusChange}
                  >
                    <option value="All">All</option>
                    <option value="Open">Open</option>
                    <option value="Assigned">Assigned</option>
                    <option value="Resolved">Resolved</option>
                    <option value="Closed">Closed</option>
                  </select>
                  <svg className="w-4 h-4 text-gray-400 absolute right-2 pointer-events-none" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Search input */}
            <div className="relative flex-grow max-w-md">
              <div className="flex items-center border border-gray-200 rounded bg-white px-3 py-1.5">
                <svg className="w-4 h-4 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                </svg>
                <input
                  type="text"
                  placeholder="Start typing to search"
                  className="w-full text-sm border-none focus:outline-none text-gray-700"
                  value={filters?.title || ""}
                  onChange={handleSearchChange}
                />
              </div>
            </div>

            {/* Clear All Filters button */}
            {hasActiveFilters() && (
              <button
                onClick={handleClearAllFilters}
                className="flex items-center text-red-500 hover:text-red-600 text-sm font-medium"
              >
                Clear Filters
              </button>
            )}
          </div>
        </div>
      </div>
      
      {/* Proper style tag instead of styleJSX which renders as literal text */}
      <style>{`
        .rdrDefinedRangesWrapper {
          width: 151px;
        }
        .rdrStaticRangeSelected {
          color: #EF4444;
          font-weight: bold;
        }
        .rdrCalendarWrapper {
          color: #000 !important;
        }
        .rdrMonthAndYearPickers select {
          color: #000;
        }
      `}</style>
    </>
  );
}
